import dayjs from 'dayjs';
import React, { useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { currencySymbols } from '../../../global/row';
import {
  ActivityBottom,
  Activity,
  TotalContents,
  ActivityContent,
  ActivityRow,
  ActivityLeft,
  Infotext,
  TextDate,
  ActivityRight
} from './styles';
import { InvoiceActivityDetailsInterface } from '../../../interfaces/InvoiceInterface';
import { deleteActivityDetail } from '../../../services/invoiceService';
import { captureException } from '../../../services/logService';
import { useParams } from 'react-router-dom';
import DeleteModal from '../../../component/modals/deleteModal';
import Modal from '../../../component/modals/modal';
import { Dropdown, DropdownItem } from '../../../component/dropdown';
import { rootStore } from '../../../mobx/rootStore';
import IconButton from '../../../component/button/iconButton';
import { ICON_BUTTON_SIZE, ICON_BUTTON_TYPE } from '../../../global/constants';

type SetState<T> = (value: T | ((prev: T) => T)) => void;

interface InvoiceActivityInterface {
  selectedItem: { id: string };
  setSelectedItem: SetState<{ id: string }>;
  loadData: () => void;
  deleteInvoiceLoading: boolean;
  setDeleteInvoiceLoading: SetState<boolean>;
  invoiceActivityDetail?: InvoiceActivityDetailsInterface[];
}

const InvoiceActivity: React.FC<InvoiceActivityInterface> = (props) => {
  const {
    selectedItem,
    setSelectedItem,
    loadData,
    deleteInvoiceLoading,
    setDeleteInvoiceLoading,
    invoiceActivityDetail
  } = props;
  //States
  const [deleteActivityModalOpen, setDeleteActivityModalOpen] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<string | number | null>(null);
  // Mobx variables
  const { currentUser, invoiceStore } = rootStore;
  const { invoiceViewData } = invoiceStore || {};
  //Other variable
  const params: { id: string } = useParams();

  // delete payment log
  const onClickDeleteActivity = useCallback(
    (activity: InvoiceActivityDetailsInterface) => {
      setSelectedItem(activity);
      setDeleteActivityModalOpen(true);
    },
    [setSelectedItem]
  );

  // handle dropdown toggle
  const handleDropdownToggle = useCallback(
    (id: string) => {
      if (openDropdownId === id) {
        setOpenDropdownId(null);
      } else {
        setOpenDropdownId(id);
      }
    },
    [openDropdownId]
  );

  // close delete activity modal
  const closeDeleteActivityModal = useCallback(() => {
    setDeleteActivityModalOpen(false);
  }, []);

  // for delete payment log
  const deletePaymentLog = useCallback(async () => {
    try {
      setDeleteInvoiceLoading(true);
      const result = await deleteActivityDetail(params?.id, selectedItem?.id);
      if (result) {
        setDeleteActivityModalOpen(false);
        rootStore.alertNotificationStore.setSuccessMessage('Invoice payment log deleted successfully!');
        loadData();
      }
    } catch (error) {
      captureException(error);
    } finally {
      setDeleteInvoiceLoading(false);
    }
  }, [loadData, params?.id, selectedItem?.id, setDeleteInvoiceLoading]);

  const getInfoText = useCallback(
    (invoiceItem: InvoiceActivityDetailsInterface, invoiceActivityDetail: InvoiceActivityDetailsInterface[]) => {
      if (invoiceItem?.typeId === 14) {
        const user = invoiceItem?.action === 'Update' ? invoiceItem?.user || '' : invoiceActivityDetail[0]?.user || '';

        return `${invoiceItem?.type} ${invoiceItem?.action} ${user ? 'by ' : ''} ${user}`;
      } else {
        const user = invoiceItem?.user || '';
        return `${invoiceItem?.desc} ${user ? 'by ' : ''}${user}`;
      }
    },
    []
  );

  // render invoice activity
  const renderInvoiceActivity = useMemo(() => {
    return invoiceActivityDetail?.map((invoiceItem, index) => {
      const ref = currencySymbols.find((x) => x.code === invoiceViewData?.currency);
      const symbol = ref?.symbol;
      return (
        <ActivityRow key={invoiceItem?.id}>
          <ActivityLeft>
            <Infotext>{getInfoText(invoiceItem, invoiceActivityDetail)}</Infotext>
            {invoiceItem?.notes && <TextDate>{invoiceItem?.notes}</TextDate>}
            <TextDate>
              {dayjs(invoiceItem?.date).format(currentUser?.dateFormat)} . {invoiceItem?.amount ? symbol : ''}
              {invoiceItem?.amount}
            </TextDate>
          </ActivityLeft>
          <ActivityRight>
            {invoiceItem?.typeId === 14 ? (
              ''
            ) : (
              <Dropdown
                modalTitle='Manage Activity'
                activeClassName='active'
                content={
                  <IconButton
                    iconName='three-dots-horizontal-icon'
                    size={ICON_BUTTON_SIZE.EXTRA_SMALL}
                    variant={ICON_BUTTON_TYPE.TERTIARY}
                    onClick={() => handleDropdownToggle(invoiceItem?.id)}
                  />
                }
                trigger='click'>
                <DropdownItem label='Delete' tone='critical' onClick={() => onClickDeleteActivity(invoiceItem)} />
              </Dropdown>
            )}
          </ActivityRight>
        </ActivityRow>
      );
    });
  }, [
    getInfoText,
    handleDropdownToggle,
    invoiceActivityDetail,
    invoiceViewData?.currency,
    onClickDeleteActivity,
    currentUser?.dateFormat
  ]);

  return (
    <>
      <ActivityBottom className='desktop-invoice-detail-content'>
        <Activity>
          <TotalContents>Activity</TotalContents>
          <ActivityContent>{renderInvoiceActivity}</ActivityContent>
        </Activity>
      </ActivityBottom>
      <Modal open={deleteActivityModalOpen} onClose={closeDeleteActivityModal} maxWidth={'334px'}>
        <DeleteModal
          onClose={closeDeleteActivityModal}
          onOk={deletePaymentLog}
          loading={deleteInvoiceLoading}
          modalTitle='Delete Payment Log?'
          description='Are you sure to want to delete this payment log?'
        />
      </Modal>
    </>
  );
};

export default observer(InvoiceActivity);
